import { useEffect, useRef, useState } from "react";
import { AlertTriangle, HelpCircle, Info, X } from "lucide-react";

const DIALOG_EVENT = "yansh:admin-dialog";

function requestDialog(detail) {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent(DIALOG_EVENT, {
      detail: { ...detail, resolve },
    }));
  });
}

export function showErrorDialog(message, title = "Something went wrong") {
  return requestDialog({ type: "error", title, message, confirmLabel: "OK" });
}

export function showAlertDialog(message, title = "Notice") {
  return requestDialog({ type: "alert", title, message, confirmLabel: "OK" });
}

export function confirmDialog(message, options = {}) {
  return requestDialog({
    type: options.tone === "danger" ? "danger" : "confirm",
    title: options.title || "Confirm action",
    message,
    confirmLabel: options.confirmLabel || "Confirm",
    cancelLabel: options.cancelLabel || "Cancel",
  });
}

export function promptDialog(message, options = {}) {
  return requestDialog({
    type: "prompt",
    title: options.title || "Additional details",
    message,
    confirmLabel: options.confirmLabel || "Continue",
    cancelLabel: options.cancelLabel || "Cancel",
    placeholder: options.placeholder || "",
    initialValue: options.initialValue || "",
    inputType: options.inputType || "text",
  });
}

export default function DialogViewport() {
  const [queue, setQueue] = useState([]);
  const [value, setValue] = useState("");
  const recentRef = useRef(new Map());
  const active = queue[0] || null;

  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      const key = `${detail.type}:${detail.title}:${detail.message}`;
      const now = Date.now();
      const previous = recentRef.current.get(key) || 0;
      // a prompt is answered by a person each time (the same password prompt can follow a wrong answer at once): only notices are de-duplicated
      if (detail.type !== "prompt" && now - previous < 4000) {
        detail.resolve?.(false);
        return;
      }
      recentRef.current.set(key, now);
      setQueue((current) => [...current, { ...detail, id: `${now}-${Math.random()}` }]);
    };
    window.addEventListener(DIALOG_EVENT, handler);
    return () => window.removeEventListener(DIALOG_EVENT, handler);
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      const message = reason?.response?.data?.message || reason?.message;
      if (message) showErrorDialog(message);
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }, []);

  useEffect(() => {
    setValue(active?.initialValue || "");
  }, [active?.id]);

  useEffect(() => {
    if (!active) return undefined;
    const keydown = (event) => {
      if (event.key === "Escape" && !["alert", "error"].includes(active.type)) {
        active.resolve?.(active.type === "prompt" ? null : false);
        setQueue((current) => current.slice(1));
      }
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [active]);

  if (!active) return null;

  const close = (confirmed) => {
    const result = active.type === "prompt"
      ? confirmed ? (active.inputType === "password" ? value : value.trim()) : null
      : ["alert", "error"].includes(active.type) ? true : Boolean(confirmed);
    active.resolve?.(result);
    setQueue((current) => current.slice(1));
  };
  const Icon = active.type === "error" || active.type === "danger"
    ? AlertTriangle
    : active.type === "confirm" || active.type === "prompt" ? HelpCircle : Info;

  return (
    <div className="app-dialog-overlay" role="presentation">
      <section className={`app-dialog app-dialog-${active.type}`} role="dialog" aria-modal="true" aria-labelledby="app-dialog-title">
        <header>
          <div className="app-dialog-icon"><Icon size={20} /></div>
          <div>
            <h2 id="app-dialog-title">{active.title}</h2>
            <p>{active.message}</p>
          </div>
          {!["alert", "error"].includes(active.type) && (
            <button type="button" className="app-dialog-close" onClick={() => close(false)} aria-label="Close"><X size={18} /></button>
          )}
        </header>
        {active.type === "prompt" && (
          <input
            autoFocus
            type={active.inputType === "password" ? "password" : "text"}
            autoComplete={active.inputType === "password" ? "current-password" : undefined}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && value.trim()) close(true);
            }}
            placeholder={active.placeholder}
          />
        )}
        <footer>
          {!["alert", "error"].includes(active.type) && (
            <button type="button" className="secondary-button" onClick={() => close(false)}>{active.cancelLabel}</button>
          )}
          <button
            type="button"
            className={active.type === "danger" ? "danger-button" : ""}
            onClick={() => close(true)}
            disabled={active.type === "prompt" && !value.trim()}
          >
            {active.confirmLabel}
          </button>
        </footer>
      </section>
    </div>
  );
}
